import { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Loader } from 'components/loader';
import { loadGridState, saveGridState } from 'utils/storage';
import {
  sanitizeGridState,
  sanitizeGridSearch,
} from 'utils/user-table-columns';

import { GridStateSyncWrapperProps } from './types';

export const GridStateSyncWrapper = ({
  version,
  children,
  storageKey,
  allowedFields,
  enabled = true,
  resetPage = false,
}: GridStateSyncWrapperProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);
  const hasSynced = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setIsReady(true);
      return;
    }

    if (allowedFields) {
      const sanitized = sanitizeGridSearch(
        location.search,
        allowedFields,
        resetPage && !hasSynced.current
      );
      // Compare the same normalized representation so equivalent URL encoding
      // cannot trigger repeated redirects. Later history navigation keeps its page.
      if (sanitized !== new URLSearchParams(location.search).toString()) {
        navigate(`${location.pathname}${sanitized ? `?${sanitized}` : ''}`, {
          replace: true,
        });
        return;
      }
    }

    if (allowedFields && !hasSynced.current) {
      const saved = loadGridState(storageKey, version);
      if (saved)
        saveGridState(
          storageKey,
          sanitizeGridState(saved.state, allowedFields),
          version
        );
    }

    if (hasSynced.current) {
      setIsReady(true);
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const hasParams =
      searchParams.has('sort') ||
      searchParams.has('filter') ||
      searchParams.has('page') ||
      searchParams.has('pageSize');

    if (!hasParams) {
      try {
        const saved = loadGridState(storageKey, version);

        if (saved) {
          const state = allowedFields
            ? sanitizeGridState(saved.state, allowedFields)
            : saved.state;
          let updated = false;

          const sortModel = state.sorting?.sortModel;
          if (sortModel && sortModel.length > 0) {
            searchParams.set('sort', JSON.stringify(sortModel));
            updated = true;
          }

          const filterModel = state.filter?.filterModel;
          if (filterModel) {
            if (
              (filterModel.items && filterModel.items.length > 0) ||
              (filterModel.quickFilterValues &&
                filterModel.quickFilterValues.length > 0)
            ) {
              searchParams.set('filter', JSON.stringify(filterModel));
              updated = true;
            }
          }

          const paginationModel = state.pagination?.paginationModel;
          if (paginationModel && !resetPage) {
            const isFirstPage =
              !paginationModel.page || paginationModel.page === 0;

            if (!isFirstPage) {
              if (typeof paginationModel.page === 'number') {
                searchParams.set('page', String(paginationModel.page));
                updated = true;
              }

              if (typeof paginationModel.pageSize === 'number') {
                searchParams.set('pageSize', String(paginationModel.pageSize));
                updated = true;
              }
            }
          }

          if (updated) {
            const newSearch = searchParams.toString();
            const newUrl = `${location.pathname}?${newSearch}`;
            navigate(newUrl, { replace: true });
            // Exit early - component will re-render after navigation
            return;
          }
        }
      } catch (error) {
        console.error(
          '[GridStateSync] Failed to restore state from localStorage:',
          error
        );
      }
    }

    hasSynced.current = true;
    setIsReady(true);
  }, [
    allowedFields,
    resetPage,
    location.pathname,
    location.search,
    navigate,
    storageKey,
    enabled,
    version,
  ]);

  if (!isReady) {
    return (
      <div className="relative h-full w-full">
        <Loader isLoading absolutePosition />
      </div>
    );
  }

  return children;
};
